import type * as React from "react"
import { Compass } from "lucide-react"

export function HelloRoute(): JSX.Element {
  return (
    <div className="mx-auto max-w-5xl px-8.5 py-11">
      <header className="mb-8.5">
        <div className="mb-5 inline-flex items-center gap-2.5 rounded-full border border-border bg-surface px-3.5 py-1.5 text-caption font-medium uppercase tracking-[0.16em] text-text-3">
          <span className="size-1.5 rounded-full bg-master" />
          Foundation ready
        </div>
        <h1 className="text-display text-text">Welcome to Pyxis</h1>
        <p className="mt-3.5 max-w-2xl text-body text-text-2">
          The new console shell is running on the foundation layer — design
          tokens, four-group role-scoped sidebar, dark-mode via{" "}
          <code className="font-id text-body-sm text-text">.dark</code> on the root,
          and a route-aware AppShell. The queue lands at Phase 4; the clients
          page at Phase 5. Everything below is a live check that the
          foundation is doing what it promises.
        </p>
      </header>

      <div className="grid gap-5 lg:grid-cols-3">
        <FoundationCard title="Design tokens" note="src/styles/tokens.css">
          <ul className="mt-3.5 space-y-2 text-body-sm">
            <PaletteRow
              className="bg-brand"
              label="brand"
              hint="wordmark, one primary per screen"
            />
            <PaletteRow
              className="bg-master"
              label="master"
              hint="settled, fixed, the approved side"
            />
            <PaletteRow
              className="bg-print"
              label="print"
              hint="in progress, needs attention"
            />
            <PaletteRow
              className="bg-err"
              label="err"
              hint="severity outline only, never filled"
            />
            <PaletteRow
              className="bg-info"
              label="info"
              hint="people, process, specialists"
            />
          </ul>
        </FoundationCard>

        <FoundationCard title="Typography" note="Instrument Sans · IBM Plex Mono">
          <div className="mt-3.5 space-y-2">
            <p className="text-title text-text">Title · 21px</p>
            <p className="text-subtitle text-text-2">Subtitle · 17px</p>
            <p className="text-heading text-text-2">Heading · 14.5px</p>
            <p className="text-body text-text-2">Body · 13.5px — the default</p>
            <p className="text-caption text-text-3">Caption · 11.5px</p>
            <p className="font-id text-eyebrow uppercase text-text-3">EYEBROW · 10.5PX</p>
          </div>
        </FoundationCard>

        <FoundationCard title="What ships next" note="docs/PLAYBOOK.md">
          <ol className="mt-3.5 space-y-2.5 text-body-sm text-text-2">
            <PhaseRow
              n="3"
              title="Primitives"
              hint="DataTable, StatCard, Combobox, Dialog…"
            />
            <PhaseRow
              n="4"
              title="Review queue"
              hint="features/queue — the real page"
            />
            <PhaseRow n="5" title="Clients" hint="features/clients — engagements + members" />
            <PhaseRow n="6" title="Cutover" hint="promote web-v2 into web/" />
          </ol>
        </FoundationCard>
      </div>

      <footer className="mt-8.5 flex items-center gap-2 text-caption text-text-3">
        <Compass className="size-3.5" strokeWidth={1.5} />
        Pyxis · Label Review · web-v2 rebuild · Phase 3 —{" "}
        <a
          href="/dev/primitives"
          className="font-medium text-brand underline-offset-2 hover:underline"
        >
          primitives gallery
        </a>
      </footer>
    </div>
  )
}

function FoundationCard({
  title,
  note,
  children,
}: {
  title: string
  note: string
  children: React.ReactNode
}): JSX.Element {
  return (
    <section className="rounded-lg border border-border bg-surface p-5 shadow-e1">
      <div className="flex items-baseline justify-between">
        <h2 className="text-heading text-text">{title}</h2>
        <span className="font-id text-caption text-text-3">{note}</span>
      </div>
      {children}
    </section>
  )
}

function PaletteRow({
  className,
  label,
  hint,
}: {
  className: string
  label: string
  hint: string
}): JSX.Element {
  return (
    <li className="flex items-start gap-2.5">
      <span className={`mt-1 inline-block size-3 shrink-0 rounded-sm ${className}`} />
      <span className="min-w-0 flex-1">
        <span className="font-medium text-text">{label}</span>
        <span className="ml-2 text-text-3">{hint}</span>
      </span>
    </li>
  )
}

function PhaseRow({
  n,
  title,
  hint,
}: {
  n: string
  title: string
  hint: string
}): JSX.Element {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-brand-tint font-id text-micro font-semibold text-brand">
        {n}
      </span>
      <span className="min-w-0 flex-1">
        <span className="font-medium text-text">{title}</span>
        <span className="ml-2 text-text-3">{hint}</span>
      </span>
    </li>
  )
}
